import React, { type PropsWithChildren, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Pressable from '@atlaskit/primitives/pressable';

const getComponentId = (groupId: string) => `filter-group-${groupId.toLowerCase()}`;

type AccordionItemProps = {
	defaultIsExpanded?: boolean;
	triggerComponent: React.ReactNode;
	hasSelection?: boolean;
	onClick?: (openState: boolean) => void;
	groupId: string;
	/** Forces open on initial mount only; defaultIsExpanded still drives resize collapse/expand. */
	isInitiallyExpanded?: boolean;
};

interface AccordionProviderProps {
	defaultIsExpanded: boolean;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	displayChevron: boolean;
	hasSelection: boolean;
}

const AccordionContext = React.createContext<AccordionProviderProps>({
	defaultIsExpanded: false,
	isOpen: false,
	setIsOpen: () => {},
	displayChevron: false,
	hasSelection: false,
});

const accordionPressableStyles = xcss({
	background: 'none', // Remove Pressable default background
	display: 'flex',
	width: '100%',
	padding: '0', // Remove Pressable default padding
});

const accordionItemsStyles = xcss({
	paddingLeft: 'space.150',
});

const AccordionItem = ({
	children,
	triggerComponent,
	onClick,
	groupId,
	defaultIsExpanded = false,
	hasSelection = false,
	isInitiallyExpanded = false,
}: PropsWithChildren<AccordionItemProps>): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(defaultIsExpanded || isInitiallyExpanded);
	const [displayChevron, setDisplayChevron] = useState(false);
	const hasUserInteractedRef = useRef(false);
	const prevDefaultIsExpandedRef = useRef(defaultIsExpanded);
	useEffect(() => {
		// isInitiallyExpanded is true only in single-select cohort — used as the gate here
		// to avoid recording experiment exposures on every accordion mount.
		if (isInitiallyExpanded) {
			// Auto-collapse when viewport shrinks (defaultIsExpanded: true→false),
			// unless the user has manually interacted — in which case their preference is preserved.
			if (
				!hasUserInteractedRef.current &&
				prevDefaultIsExpandedRef.current === true &&
				defaultIsExpanded === false
			) {
				setIsOpen(false);
			}

			// Auto-expand when defaultIsExpanded changes from false to true (viewport expands)
			// Only expand if user hasn't interacted (to preserve their choice of closing it)
			if (
				!hasUserInteractedRef.current &&
				prevDefaultIsExpandedRef.current === false &&
				defaultIsExpanded === true
			) {
				setIsOpen(true);
			}
		} else {
			setIsOpen(defaultIsExpanded);
		}

		prevDefaultIsExpandedRef.current = defaultIsExpanded;
	}, [defaultIsExpanded, isInitiallyExpanded]);

	return (
		<AccordionProvider
			defaultIsExpanded={defaultIsExpanded}
			isOpen={isOpen}
			setIsOpen={setIsOpen}
			displayChevron={displayChevron}
			hasSelection={hasSelection}
		>
			<Pressable
				aria-expanded={isOpen}
				aria-current={!isOpen && hasSelection} // It is current when the Accordion is collapsed and one of the nested apps is selected
				id={getComponentId(groupId)}
				testId={getComponentId(groupId)}
				xcss={accordionPressableStyles}
				onBlur={() => setDisplayChevron(false)}
				onFocus={() => setDisplayChevron(true)}
				onMouseOver={() => setDisplayChevron(true)}
				onMouseLeave={() => setDisplayChevron(false)}
				onClick={() => {
					const newIsOpen = !isOpen;
					hasUserInteractedRef.current = true; // Mark that user has interacted
					setIsOpen(() => newIsOpen);
					if (onClick && newIsOpen) {
						onClick(newIsOpen);
					}
				}}
			>
				{triggerComponent}
			</Pressable>
			<Box role="list" xcss={accordionItemsStyles}>
				{isOpen && children}
			</Box>
		</AccordionProvider>
	);
};

function AccordionProvider(props: PropsWithChildren<AccordionProviderProps>) {
	return <AccordionContext.Provider value={props}>{props.children}</AccordionContext.Provider>;
}

function useAccordion() {
	const context = React.useContext(AccordionContext);

	if (context === undefined) {
		throw new Error('useAccordion must be used within a AccordionContext');
	}
	return context;
}

export { AccordionItem, type AccordionItemProps, type AccordionProviderProps, useAccordion };
